/**
 * MarketingSegmentenPage — smart segments (read-only regels + herbereken) en
 * handmatige lijsten (aanmaken; leden beheren via contacten-zoektabel).
 */
import { useEffect, useState } from 'react';
import { useMarketingStore, marketingFetch, type MarketingSegment } from '../../stores/marketing-store';
import { MarketingNav, MarketingPageHeader, SectionCard, formatDateTime } from './shared';

const OP_LABELS: Record<string, string> = {
  eq: '=',
  neq: '≠',
  gt: '>',
  gte: '≥',
  lt: '<',
  lte: '≤',
  contains: 'bevat',
  in: 'in',
  is_null: 'is leeg',
  not_null: 'is niet leeg',
};

function definitionLabel(segment: MarketingSegment): string {
  const rules = segment.definition?.all ?? [];
  if (rules.length === 0) return 'Alle contacten';
  return rules.map((r) => `${r.field} ${OP_LABELS[r.op] ?? r.op} ${r.value ?? ''}`.trim()).join(' én ');
}

export function MarketingSegmentenPage() {
  const { segments, error, fetchSegments } = useMarketingStore();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [showNew, setShowNew] = useState(false);
  const [newKey, setNewKey] = useState('');
  const [newName, setNewName] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSegments();
  }, [fetchSegments]);

  const recompute = async (id: string) => {
    setBusyId(id);
    setActionError(null);
    try {
      await marketingFetch(`/api/marketing/segments?id=${id}&action=recompute`, { method: 'POST' });
      await fetchSegments();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Herberekenen mislukt');
    } finally {
      setBusyId(null);
    }
  };

  const createManual = async () => {
    setSaving(true);
    setActionError(null);
    try {
      await marketingFetch('/api/marketing/segments', {
        method: 'POST',
        body: { key: newKey, name: newName, type: 'manual' },
      });
      setShowNew(false);
      setNewKey('');
      setNewName('');
      await fetchSegments();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Aanmaken mislukt');
    } finally {
      setSaving(false);
    }
  };

  const smart = segments.filter((s) => s.type === 'smart');
  const manual = segments.filter((s) => s.type === 'manual');

  return (
    <>
      <div className="flex-1 overflow-y-auto">
        <MarketingPageHeader
          title="Segmenten"
          subtitle="Smart segments worden nachtelijks gematerialiseerd; handmatige lijsten beheer je zelf"
          actions={
            <button onClick={() => setShowNew(true)} className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700">
              + Handmatige lijst
            </button>
          }
        />
        <MarketingNav />

        <div className="p-6 space-y-6">
          {(error || actionError) && (
            <div className="bg-red-50 text-red-700 text-sm rounded-lg px-4 py-3">{actionError ?? error}</div>
          )}

          <SectionCard title="Smart segments">
            <div className="divide-y divide-gray-100">
              {smart.map((s) => (
                <div key={s.id} className="py-3 flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900">{s.name}</p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {definitionLabel(s)} · laatst berekend: {formatDateTime(s.last_computed_at)}
                    </p>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className="text-sm font-semibold text-gray-900">{s.member_count ?? '—'}</span>
                    <button
                      onClick={() => recompute(s.id)}
                      disabled={busyId !== null}
                      className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                    >
                      {busyId === s.id ? 'Bezig…' : 'Herbereken'}
                    </button>
                  </div>
                </div>
              ))}
              {smart.length === 0 && <p className="text-sm text-gray-400 py-3">Geen smart segments.</p>}
            </div>
          </SectionCard>

          <SectionCard title="Handmatige lijsten">
            <div className="divide-y divide-gray-100">
              {manual.map((s) => (
                <div key={s.id} className="py-3 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{s.name}</p>
                    <p className="text-xs text-gray-400 mt-0.5">key: {s.key}</p>
                  </div>
                  <span className="text-sm font-semibold text-gray-900">{s.member_count ?? 0}</span>
                </div>
              ))}
              {manual.length === 0 && (
                <p className="text-sm text-gray-400 py-3">
                  Geen handmatige lijsten. Handig voor testcampagnes (eigen e-mailadressen) of eenmalige selecties — leden
                  voeg je toe vanuit de Contacten-pagina.
                </p>
              )}
            </div>
          </SectionCard>
        </div>
      </div>

      {showNew && (
        <div className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center p-4" onClick={() => setShowNew(false)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Nieuwe handmatige lijst</h2>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Key (uniek, kebab-case)</label>
                <input type="text" value={newKey} onChange={(e) => setNewKey(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="test-eigen-adressen" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Naam</label>
                <input type="text" value={newName} onChange={(e) => setNewName(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="Test — eigen adressen" />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setShowNew(false)} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900">
                Annuleren
              </button>
              <button
                onClick={createManual}
                disabled={saving || !newKey || !newName}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              >
                {saving ? 'Aanmaken…' : 'Aanmaken'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
